import type { IDealerFee, ItemizedGovernmentFee, OfferType } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';

import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import { getIncludedInCalc } from '../selectors/feesSelectors';
import { getSelectedTerm } from '../selectors/offerNodeSelectors';
import {
    getCapCostReductionByOfferType,
    getCashDueFromCustomerByOfferType,
    getCashOffsetAmountAddedToPriceByOfferType,
    getDiaAccessoriesByOfferType,
    getDiaProtectionProductsByOfferType,
    getDiaTaxesByOfferType,
    getFirstPaymentByOfferType,
    getFirstPaymentCapitalizedByOfferType,
    getNetTradeByOfferType,
    getPaidAtSigningByOfferType,
    getRebateByOfferType,
    getSecurityDepositByOfferType,
    getTradeDownByOfferType,
    getUpfrontPriorLeaseBalanceByOfferType,
    isLeaseDueAtSigningSectionEnabled
} from '../selectors/selectedTermSelectors';

export type LeaseDueAtSigningLineKey =
    | 'firstMonthPayment'
    | 'taxes'
    | 'lenderFee'
    | 'dealerFee'
    | 'governmentFee'
    | 'protectionProducts'
    | 'accessories'
    | 'priorLeaseBalance'
    | 'securityDeposit'
    | 'capCostReduction'
    | 'remainderAddedToLeasePrice'
    | 'cashDueFromCustomer'
    | 'rebate'
    | 'netTradeInAllowance';

export type LeaseDueAtSigningLine = {
    key: LeaseDueAtSigningLineKey;
    name: string;
    value: number;
};

export type LeaseDueAtSigningSection = {
    items: LeaseDueAtSigningLine[];
    total: number;
};

export type LeaseDueAtSigningBreakdown = {
    cashDueFromCustomer: number;
    estTotalDueAtSigning: number;
    dueAtSigning: LeaseDueAtSigningSection;
    paidBy: LeaseDueAtSigningSection;
};

export type LeaseDueAtSigningFee = {
    name: string;
    amount: number;
};

export type LeaseDueAtSigningInputs = {
    paidAtSigning: number;
    cashDueFromCustomer: number;
    firstPayment: number;
    firstPaymentCapitalized: boolean;
    taxes: number;
    lenderFees: LeaseDueAtSigningFee[];
    dealerFees: LeaseDueAtSigningFee[];
    governmentFees: LeaseDueAtSigningFee[];
    protectionProducts: number;
    accessories: number;
    priorLeaseBalance: number;
    securityDeposit: number;
    capCostReduction: number;
    cashOffsetAmountAddedToPrice: number;
    rebate: number;
    tradeDown: number;
    netTrade: number;
};

export const toCents = (value: number): number => Math.round(value * 100);

export const sumLines = (items: LeaseDueAtSigningLine[]): number =>
    items.reduce((cents, item) => cents + toCents(item.value), 0) / 100;

const line = (key: LeaseDueAtSigningLineKey, name: string, value: number): LeaseDueAtSigningLine => ({ key, name, value });

const feeLines = (key: LeaseDueAtSigningLineKey, fees: LeaseDueAtSigningFee[]): LeaseDueAtSigningLine[] =>
    fees.map((fee) => line(key, fee.name, fee.amount));

export const buildLeaseDueAtSigningBreakdown = (inputs: LeaseDueAtSigningInputs): LeaseDueAtSigningBreakdown => {
    const dueAtSigningItems = [
        line('firstMonthPayment', 'First Month Payment', inputs.firstPaymentCapitalized ? 0 : inputs.firstPayment),
        line('taxes', 'Taxes', inputs.taxes),
        ...feeLines('lenderFee', inputs.lenderFees),
        ...feeLines('dealerFee', inputs.dealerFees),
        ...feeLines('governmentFee', inputs.governmentFees),
        line('protectionProducts', 'Protection Products', inputs.protectionProducts),
        line('accessories', 'Accessories', inputs.accessories),
        line('priorLeaseBalance', 'Prior Lease Balance', inputs.priorLeaseBalance),
        line('securityDeposit', 'Security Deposit', inputs.securityDeposit),
        line('capCostReduction', 'Capitalized Cost Reduction', inputs.capCostReduction)
    ].filter((item) => item.value > 0);

    if (inputs.cashOffsetAmountAddedToPrice > 0) {
        dueAtSigningItems.push(
            line('remainderAddedToLeasePrice', 'Remainder Added to Lease Price', -inputs.cashOffsetAmountAddedToPrice)
        );
    }

    const paidByItems = [
        line('cashDueFromCustomer', 'Cash Due From Customer', inputs.cashDueFromCustomer),
        line('rebate', 'Rebate', inputs.rebate),
        line('netTradeInAllowance', 'Net Trade-In Allowance', inputs.netTrade < 0 ? 0 : inputs.tradeDown)
    ].filter((item) => item.value > 0);

    return {
        cashDueFromCustomer: inputs.cashDueFromCustomer,
        estTotalDueAtSigning: inputs.paidAtSigning,
        dueAtSigning: { items: dueAtSigningItems, total: inputs.paidAtSigning },
        paidBy: { items: paidByItems, total: inputs.paidAtSigning }
    };
};

export const isLeaseDueAtSigningBreakdownBalanced = (breakdown: LeaseDueAtSigningBreakdown): boolean =>
    toCents(sumLines(breakdown.dueAtSigning.items)) === toCents(breakdown.dueAtSigning.total) &&
    toCents(sumLines(breakdown.paidBy.items)) === toCents(breakdown.paidBy.total);

const isUpfrontDealerFee = (category: string) => (fee: IDealerFee) =>
    fee.originalCategory?.toLowerCase() === category && fee.dealerFeeCapped === false && fee.dealerFeeAmount > 0;

const toDealerFeeItem = (fee: IDealerFee): LeaseDueAtSigningFee => ({ name: fee.dealerFeeName, amount: fee.dealerFeeAmount });

const isUpfrontGovernmentFee = (fee: ItemizedGovernmentFee) => !fee.capped && fee.includedInCalc && fee.amount > 0;

const toGovernmentFeeItem = (fee: ItemizedGovernmentFee): LeaseDueAtSigningFee => ({ name: fee.name, amount: fee.amount });

export const getLeaseDueAtSigningInputs = (
    state: OfferReduxBaseState,
    offerType: OfferType = LEASE
): LeaseDueAtSigningInputs | undefined => {
    const paidAtSigning = getPaidAtSigningByOfferType(state, offerType);
    const cashDueFromCustomer = getCashDueFromCustomerByOfferType(state, offerType);
    const firstPaymentCapitalized = getFirstPaymentCapitalizedByOfferType(state, offerType) ?? false;
    const firstPayment = getFirstPaymentByOfferType(state, offerType);
    const taxes = getDiaTaxesByOfferType(state, offerType);
    const protectionProducts = getDiaProtectionProductsByOfferType(state, offerType);
    const accessories = getDiaAccessoriesByOfferType(state, offerType);
    const capCostReduction = getCapCostReductionByOfferType(state, offerType);
    const cashOffsetAmountAddedToPrice = getCashOffsetAmountAddedToPriceByOfferType(state, offerType);
    const rebate = getRebateByOfferType(state, offerType);
    const tradeDown = getTradeDownByOfferType(state, offerType);

    if (
        paidAtSigning == null ||
        cashDueFromCustomer == null ||
        (firstPayment == null && !firstPaymentCapitalized) ||
        taxes == null ||
        protectionProducts == null ||
        accessories == null ||
        capCostReduction == null ||
        cashOffsetAmountAddedToPrice == null ||
        rebate == null ||
        tradeDown == null
    ) {
        return undefined;
    }

    const term = getSelectedTerm(state, offerType);
    const includedDealerFees = getIncludedInCalc(term?.dealerFees) as IDealerFee[];
    const governmentFees: ItemizedGovernmentFee[] = term?.fees?.fee?.taxFee ?? [];

    return {
        paidAtSigning,
        cashDueFromCustomer,
        firstPayment: firstPayment ?? 0,
        firstPaymentCapitalized,
        taxes,
        lenderFees: includedDealerFees.filter(isUpfrontDealerFee('lender')).map(toDealerFeeItem),
        dealerFees: includedDealerFees.filter(isUpfrontDealerFee('dealer')).map(toDealerFeeItem),
        governmentFees: governmentFees.filter(isUpfrontGovernmentFee).map(toGovernmentFeeItem),
        protectionProducts,
        accessories,
        priorLeaseBalance: getUpfrontPriorLeaseBalanceByOfferType(state, offerType),
        securityDeposit: getSecurityDepositByOfferType(state, offerType) ?? 0,
        capCostReduction,
        cashOffsetAmountAddedToPrice,
        rebate,
        tradeDown,
        netTrade: getNetTradeByOfferType(state, offerType) ?? 0
    };
};

export const getLeaseDueAtSigningBreakdown = (
    state: OfferReduxBaseState,
    offerType: OfferType = LEASE
): LeaseDueAtSigningBreakdown | undefined => {
    if (offerType !== LEASE || !isLeaseDueAtSigningSectionEnabled(state)) {
        return undefined;
    }

    const inputs = getLeaseDueAtSigningInputs(state, offerType);
    if (!inputs) {
        return undefined;
    }

    const breakdown = buildLeaseDueAtSigningBreakdown(inputs);
    return isLeaseDueAtSigningBreakdownBalanced(breakdown) ? breakdown : undefined;
};
